import { useEffect, useState } from "react";
import { ItemEntry } from "@models/item-entry";
import { useSearchItemEntriesBrowseQuery } from "@features/tickets/api/itemEntries.api";
import { ITEM_ENTRY_BROWSE_PER_PAGE, buildItemEntrySort } from "@features/tickets/api/itemEntriesBrowse.utils";
import { useErrorBoundary } from "react-error-boundary";
import { handleErrorWithBoundary } from "@shared/lib/errors";
import { DEFAULT_SORT_KEY, DEFAULT_SORT_METHOD } from "../utils/browseUrlParams";
import { useBrowsePagination } from "./useBrowsePagination";
import { isBrowseCacheEligible } from "./useBrowseCacheEligible";

export function useItemEntriesBrowse(itemId: number | undefined) {
  const { showBoundary } = useErrorBoundary();
  const [data, setData] = useState<ItemEntry[]>([]);
  const [sortKey, setSortKey] = useState(DEFAULT_SORT_KEY);
  const [sortMethod, setSortMethod] = useState<"ASC" | "DESC">(DEFAULT_SORT_METHOD);
  const pagination = useBrowsePagination(ITEM_ENTRY_BROWSE_PER_PAGE);

  const filter = itemId ? { item_id: itemId } : {};

  const {
    data: queryData,
    isFetching: isLoading,
    error,
  } = useSearchItemEntriesBrowseQuery(
    {
      itemId: itemId ?? 0,
      filter,
      sort: [buildItemEntrySort(sortKey, sortMethod)],
      per_page: pagination.perPage,
      cursor: pagination.cursor,
      useCache: isBrowseCacheEligible({
        filters: filter,
        sortKey,
        sortMethod,
        cursor: pagination.cursor,
        allowedFilterKeys: ["item_id"],
      }),
    },
    { skip: !itemId },
  );

  useEffect(() => {
    if (!itemId) {
      setData([]);
    }
  }, [itemId]);

  useEffect(() => {
    if (queryData) {
      setData(queryData.data);
    }
  }, [queryData]);

  useEffect(() => {
    if (error) {
      handleErrorWithBoundary(error, showBoundary, undefined, "backgroundQuery");
    }
  }, [error, showBoundary]);

  function handleSortKeyChanged(value: string) {
    pagination.resetPagination();
    setSortKey(value);
  }

  function handleSortMethodChanged(value: string) {
    pagination.resetPagination();
    setSortMethod(value === "ASC" ? "ASC" : "DESC");
  }

  function handleNextPage() {
    pagination.handleNextPage(queryData?.meta.next_cursor);
  }

  function handlePrevPage() {
    pagination.handlePrevPage(queryData?.meta.prev_cursor);
  }

  return {
    data,
    isLoading: Boolean(itemId) && isLoading,
    sortKey,
    sortMethod,
    handleSortKeyChanged,
    handleSortMethodChanged,
    page: pagination.page,
    perPage: pagination.perPage,
    hasNextPage: Boolean(queryData?.meta.next_cursor),
    hasPrevPage: Boolean(queryData?.meta.prev_cursor),
    handleNextPage,
    handlePrevPage,
    handleRowsPerPageChange: pagination.handleRowsPerPageChange,
  };
}
